import { isServiceWeekStart } from "./service-week";

/** Preselect carried only from an authorized caller's own picker values. */
export type TimesheetsPreselect = {
  clientId: string;
  siteId: string;
  weekStart: string;
};

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID.test(value);
}

/**
 * Deep link into Schedule → Timesheets. May carry client, site, and week only when
 * those values came from this caller's authorized picker read in the current session.
 */
export function scheduleTimesheetsPath(preselect?: TimesheetsPreselect | null): string {
  if (!preselect) return "/schedule/timesheets";
  const params = new URLSearchParams({
    client: preselect.clientId,
    site: preselect.siteId,
    week: preselect.weekStart,
  });
  return `/schedule/timesheets?${params.toString()}`;
}

/**
 * Reader for the addressable Timesheets section under `/schedule/*`.
 * Unknown or malformed query fails closed to the Timesheets root (no partial preselect).
 */
export function scheduleTimesheetsRoute(
  pathname: string,
  search: string,
): { section: "timesheets"; preselect: TimesheetsPreselect | null } | null {
  if (!/^\/schedule\/timesheets\/?$/.test(pathname)) return null;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const client = params.get("client");
  const site = params.get("site");
  const week = params.get("week");
  if (client === null && site === null && week === null) {
    return { section: "timesheets", preselect: null };
  }
  if (isUuid(client) && isUuid(site) && isServiceWeekStart(week)) {
    return {
      section: "timesheets",
      preselect: { clientId: client, siteId: site, weekStart: week },
    };
  }
  return { section: "timesheets", preselect: null };
}
